import type { ChatMessage, CheckExamReceiptYesNoPrompt } from '../types/chat'
import { isFrenchLanguage } from './chatUiLocale'

export function parseCheckExamReceiptConfirmFromMetadata(
  md: Record<string, unknown>,
): CheckExamReceiptYesNoPrompt | undefined {
  const ctx = String(md.general_confirmation_context ?? md.generalConfirmationContext ?? '')
    .trim()
    .toLowerCase()
  const awaiting =
    md.awaiting_general_confirmation === true || md.awaitingGeneralConfirmation === true
  const raw = md.check_exam_receipt_confirm ?? md.checkExamReceiptConfirm
  if (raw && typeof raw === 'object') {
    const row = raw as Record<string, unknown>
    const modality = String(row.modality ?? row.modality_label ?? row.modalityLabel ?? '').trim()
    const examDate = String(row.exam_date ?? row.examDate ?? '').trim()
    const language = String(row.language || '').trim() || undefined
    return {
      modality: modality || undefined,
      examDate: examDate || undefined,
      language,
    }
  }
  if (ctx === 'check_exam_receipt_open' && awaiting) {
    return {}
  }
  return undefined
}

export function isCheckExamReceiptConfirmationMessage(m: ChatMessage): boolean {
  if (m.checkExamReceiptYesNoPrompt) return true
  const ctx = (m.generalConfirmationContext || '').trim().toLowerCase()
  return ctx === 'check_exam_receipt_open' && m.awaitingGeneralConfirmation === true
}

export function checkExamReceiptConfirmIsActionable(m: ChatMessage, hist: boolean): boolean {
  if (hist || m.checkExamReceiptYesNoResolved) return false
  return isCheckExamReceiptConfirmationMessage(m)
}

export function checkExamReceiptUiFrench(m: ChatMessage): boolean {
  const lang = (m.checkExamReceiptYesNoPrompt?.language || '').trim().toLowerCase()
  if (lang === 'french' || lang === 'fr') return true
  return isFrenchLanguage()
}

export function markCheckExamReceiptConfirmResolved(
  messages: ChatMessage[],
  messageId?: string,
): ChatMessage[] {
  return messages.map((m) => {
    if (!isCheckExamReceiptConfirmationMessage(m) || m.checkExamReceiptYesNoResolved) return m
    if (messageId && m.id !== messageId) return m
    return {
      ...m,
      checkExamReceiptYesNoResolved: true,
      awaitingGeneralConfirmation: false,
    }
  })
}

/** Last assistant row still waiting for check-exam-receipt open Yes/No. */
export function findPendingCheckExamReceiptConfirm(
  messages: readonly ChatMessage[],
): ChatMessage | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.historySnapshot) continue
    if (m.role !== 'assistant') continue
    if (checkExamReceiptConfirmIsActionable(m, false)) return m
  }
  return null
}

export function parseCheckExamReceiptOpenConfirmAnswer(text: string): 'yes' | 'no' | null {
  const t = (text || '').trim().toLowerCase().replace(/[^\w\s']/g, ' ')
  const norm = t.replace(/\s+/g, ' ').trim()
  if (!norm) return null
  if (
    /^(no|non|nope|cancel|stop|annule|annuler)\b/.test(norm) ||
    /\b(no\s+cancel|non\s+annuler)\b/.test(norm)
  ) {
    return 'no'
  }
  if (
    /^(yes|yeah|yep|oui|ok|okay)\b/.test(norm) ||
    /\b(yes\s+open|yes\s+proceed|oui\s+ouvr|open\s+it)\b/.test(norm)
  ) {
    return 'yes'
  }
  return null
}

/** Check-exam-receipt open confirm must hit ``/chat/command`` (not host ``sendMessage``). */
export function shouldUseHostedChatForCheckExamReceiptConfirm(
  text: string,
  messages: readonly ChatMessage[],
): boolean {
  if (!findPendingCheckExamReceiptConfirm(messages)) return false
  return parseCheckExamReceiptOpenConfirmAnswer(text) !== null
}

/** Do not treat exam-receipt Yes/No as a stale patient-switch follow-up. */
export function checkExamReceiptShouldBypassPatientSwitch(
  messages: readonly ChatMessage[],
  text: string,
): boolean {
  return shouldUseHostedChatForCheckExamReceiptConfirm(text, messages)
}
